import type { FC } from 'react';
import { DatePicker, Flex, Select, Switch, Typography } from 'antd';
import { SortAscendingOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { TechnicianNameBadge } from '~/app/components/maintenance/work-orders/TechnicianNameBadge/TechnicianNameBadge';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  UNASSIGNED_TECHNICIAN_FILTER,
  type KanbanSortOption,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { KanbanSortOptionLabel } from './KanbanSortOptionLabel';
import { useWorkOrderKanbanFilterOptions } from './hooks/useWorkOrderKanbanFilterOptions';
import { useWorkOrderKanbanSortOptions } from './hooks/useWorkOrderKanbanSortOptions';

export interface WorkOrderKanbanFiltersProps {
  siteFilter: string;
  onSiteFilterChange: (value: string) => void;
  siteOptions: Array<{ value: string; label: string }>;
  technicianFilter: string;
  onTechnicianFilterChange: (value: string) => void;
  technicianOptions: Array<{ value: string; label: string }>;
  planFilter: string;
  onPlanFilterChange: (value: string) => void;
  planOptions: Array<{ value: string; label: string }>;
  kanbanSort: KanbanSortOption;
  onKanbanSortChange: (value: KanbanSortOption) => void;
  dueRange: [Dayjs | null, Dayjs | null] | null;
  onDueRangeChange: (range: [Dayjs | null, Dayjs | null] | null) => void;
  followUpOnly: boolean;
  onFollowUpOnlyChange: (value: boolean) => void;
}

export const WorkOrderKanbanFilters: FC<WorkOrderKanbanFiltersProps> = ({
  siteFilter,
  onSiteFilterChange,
  siteOptions,
  technicianFilter,
  onTechnicianFilterChange,
  technicianOptions,
  planFilter,
  onPlanFilterChange,
  planOptions,
  kanbanSort,
  onKanbanSortChange,
  dueRange,
  onDueRangeChange,
  followUpOnly,
  onFollowUpOnlyChange,
}) => {
  const { t } = useTranslation('maintenance');
  const options = useWorkOrderKanbanFilterOptions({
    siteOptions,
    technicianOptions,
    planOptions,
  });
  const { groups: sortGroups, flatOptions: sortOptions } =
    useWorkOrderKanbanSortOptions();
  const selectedSort =
    sortOptions.find((option) => option.value === kanbanSort) ?? sortOptions[0];

  return (
    <Flex gap={10} wrap="wrap" align="center">
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ minWidth: 180 }}
        placeholder={t('workOrders.kanban.filters.allSites')}
        value={siteFilter || undefined}
        options={options.siteOptions}
        onChange={(value) => onSiteFilterChange(value ?? '')}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: 200 }}
        placeholder={t('workOrders.kanban.filters.allTechnicians')}
        value={technicianFilter || undefined}
        options={options.technicianOptions}
        optionRender={(option) => {
          const name = String(option.label ?? '');
          if (option.value === UNASSIGNED_TECHNICIAN_FILTER) {
            return name;
          }
          return <TechnicianNameBadge name={name} />;
        }}
        labelRender={(props) => {
          const name = String(props.label ?? '');
          if (props.value === UNASSIGNED_TECHNICIAN_FILTER) {
            return name;
          }
          return <TechnicianNameBadge name={name} />;
        }}
        onChange={(value) => onTechnicianFilterChange(value ?? '')}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: 220 }}
        popupMatchSelectWidth={false}
        placeholder={t('workOrders.kanban.filters.allPlans')}
        value={planFilter || undefined}
        options={options.planOptions}
        onChange={(value) => onPlanFilterChange(value ?? '')}
      />
      <Flex align="center" gap={6}>
        <Typography.Text
          type="secondary"
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: WORK_ORDER_UI.sidebarNavy,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            whiteSpace: 'nowrap',
          }}
        >
          <SortAscendingOutlined aria-hidden />
          {t('workOrders.kanban.filters.sortLabel')}
        </Typography.Text>
        <Select
          style={{ minWidth: 230 }}
          popupMatchSelectWidth={false}
          value={kanbanSort}
          options={sortGroups.map((group) => ({
            label: group.label,
            options: group.options.map((option) => ({
              value: option.value,
              label: option.label,
            })),
          }))}
          optionRender={(option) => {
            const visual = sortOptions.find(
              (item) => item.value === option.value,
            );
            if (!visual) return option.label;
            return (
              <KanbanSortOptionLabel
                icon={visual.icon}
                label={visual.label}
                color={visual.color}
              />
            );
          }}
          labelRender={() =>
            selectedSort ? (
              <KanbanSortOptionLabel
                compact
                icon={selectedSort.icon}
                label={selectedSort.label}
                color={selectedSort.color}
              />
            ) : null
          }
          onChange={(value) => onKanbanSortChange(value)}
          aria-label={t('workOrders.kanban.filters.sortLabel')}
        />
      </Flex>
      <DatePicker.RangePicker
        allowEmpty={[true, true]}
        style={{ minWidth: 260 }}
        value={dueRange}
        onChange={(dates) => {
          if (!dates || (!dates[0] && !dates[1])) {
            onDueRangeChange(null);
            return;
          }
          onDueRangeChange([dates[0] ?? null, dates[1] ?? null]);
        }}
        placeholder={[
          t('workOrders.kanban.filters.dueFrom'),
          t('workOrders.kanban.filters.dueTo'),
        ]}
      />
      <Flex gap={8} align="center">
        <Switch checked={followUpOnly} onChange={onFollowUpOnlyChange} />
        <Typography.Text style={{ fontSize: 13 }}>
          {t('workOrders.kanban.filters.followUpOnly')}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};
